'use client';

import Link from 'next/link';
import { useEffect, useRef, useState, type FormEvent, type ChangeEvent } from 'react';
import { ArrowLeft, Camera, CheckCircle2, Loader2, Star, X } from 'lucide-react';
import { publicReviewSchema } from '@/lib/tours/review-submission';
import { getDateInTimeZoneISO } from '@/lib/dateUtils';
import { prepareReviewPhotoPreview } from '@/lib/tours/review-photo-preview';

const labels = ['Muy mala', 'Mala', 'Regular', 'Buena', 'Excelente'];
const inputClass = 'mt-2 w-full rounded-xl border border-gray-300 bg-white px-3.5 py-3 text-base text-gray-900 outline-none focus:border-primary-600 focus:ring-2 focus:ring-primary-600/20';

export function TourReviewForm({ publicSlug, tourSlug, tourName, brandName = 'Pa’Cartagena', backHref, privacyHref = '/privacidad', endpoint = '/api/tours/reviews' }: {
  publicSlug: string; tourSlug: string; tourName: string;
  brandName?: string; backHref?: string; privacyHref?: string; endpoint?: string;
}) {
  const experienceHref = backHref ?? `/tours/${tourSlug}`;
  const sharedNakawa = tourSlug === 'nakawa-beach';
  const [rating, setRating] = useState(0);
  const [busy, setBusy] = useState(false);
  const [received, setReceived] = useState(false);
  const [error, setError] = useState('');
  const submitting = useRef(false);
  const messageRef = useRef<HTMLDivElement>(null);
  const [photos, setPhotos] = useState<Array<{ file: File; url: string }>>([]);
  const [preparingPhotos, setPreparingPhotos] = useState(false);
  const photoUrls = useRef<string[]>([]);
  useEffect(() => () => photoUrls.current.forEach(url => URL.revokeObjectURL(url)), []);

  async function addPhotos(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (photos.length + files.length > 3) { setError('Puedes adjuntar hasta 3 fotos.'); return; }
    setPreparingPhotos(true);
    setError('');
    try {
      const prepared: File[] = [];
      for (const file of files) prepared.push(await prepareReviewPhotoPreview(file));
      const additions = prepared.map(file => ({ file, url: URL.createObjectURL(file) }));
      photoUrls.current.push(...additions.map(photo => photo.url));
      setPhotos(previous => [...previous, ...additions]);
    } catch (error) { setError((error as Error).message); }
    finally { setPreparingPhotos(false); }
  }

  function removePhoto(url: string) {
    URL.revokeObjectURL(url);
    photoUrls.current = photoUrls.current.filter(value => value !== url);
    setPhotos(previous => previous.filter(photo => photo.url !== url));
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting.current || preparingPhotos) return;
    const data = new FormData(event.currentTarget);
    const payload = {
      slug: publicSlug, rating,
      authorDisplayName: data.get('authorDisplayName'),
      reviewText: data.get('reviewText'), tourDate: data.get('tourDate'),
      contact: data.get('contact'), consentTextPublic: data.get('consentTextPublic') === 'on',
      consentPhotoPublic: photos.length > 0 && data.get('consentPhotoPublic') === 'on',
      _hp: data.get('_hp'),
    };
    const parsed = publicReviewSchema().safeParse(payload);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Revisa los datos de tu reseña.');
      requestAnimationFrame(() => messageRef.current?.focus());
      return;
    }
    submitting.current = true;
    setBusy(true);
    setError('');
    try {
      const form = new FormData();
      form.set('review', JSON.stringify(parsed.data));
      photos.forEach(photo => form.append('photos', photo.file));
      const response = await fetch(endpoint, {
        method: 'POST', headers: photos.length ? {} : { 'Content-Type': 'application/json' },
        body: photos.length ? form : JSON.stringify(parsed.data), signal: AbortSignal.timeout(90000),
      });
      const result = await response.json();
      if (!response.ok || !result.received) throw new Error(result.error || 'No pudimos guardar tu reseña. Inténtalo de nuevo.');
      setReceived(true);
    } catch (err) {
      setError(err instanceof Error && err.name === 'Error' ? err.message : 'No se pudo confirmar el envío. Tu texto sigue aquí; inténtalo de nuevo.');
    } finally {
      submitting.current = false;
      setBusy(false);
      requestAnimationFrame(() => messageRef.current?.focus());
    }
  }

  return (
    <div className="min-h-svh bg-[#faf9f6] px-5 py-7 sm:py-12">
      <div className="mx-auto max-w-xl">
        <Link href={experienceHref} className="inline-flex min-h-11 items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900">
          <ArrowLeft size={16} aria-hidden /> Ver la experiencia
        </Link>
        <header className="mb-8 mt-5">
          <p className="text-sm font-semibold text-primary-700">{brandName} · Opiniones de viajeros</p>
          <h1 className="mt-3 font-serif text-3xl font-semibold leading-tight text-gray-950 sm:text-4xl">¿Cómo fue tu experiencia?</h1>
          <p className="mt-3 text-lg font-medium text-gray-800">{tourName}</p>
          <p className="mt-2 text-base leading-relaxed text-gray-600">Tu opinión ayuda a otros viajeros a elegir. Cuéntanos con tus palabras qué tal te fue.</p>
        </header>

        {received ? (
          <div ref={messageRef} tabIndex={-1} role="status" className="rounded-2xl border border-emerald-200 bg-white p-7 outline-none">
            <CheckCircle2 className="h-8 w-8 text-emerald-700" aria-hidden />
            <h2 className="mt-4 text-2xl font-semibold text-gray-900">Gracias por compartir tu experiencia</h2>
            <p className="mt-3 leading-relaxed text-gray-600">Recibimos tu reseña. Comprobaremos tu participación antes de publicarla en la ficha de este tour. Si necesitamos un dato, te contactaremos en privado.</p>
            <Link href={backHref ?? `/tours/${tourSlug}#tour-first-party-reviews`} className="mt-6 inline-flex min-h-11 items-center font-semibold text-primary-700 underline underline-offset-4">Volver a la experiencia</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="ph-no-capture ph-no-record space-y-7" aria-busy={busy}>
            <fieldset disabled={busy}>
              <legend className="text-base font-semibold text-gray-900">Tu calificación</legend>
              <div className="mt-3 flex gap-2 sm:gap-3">
                {labels.map((label, index) => (
                  <label key={label} className="relative cursor-pointer">
                    <input type="radio" name="rating" value={index + 1} required checked={rating === index + 1} onChange={() => setRating(index + 1)} className="peer sr-only" aria-label={`${index + 1} de 5: ${label}`} />
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-gray-300 bg-white peer-checked:border-amber-600 peer-checked:bg-amber-50 peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-primary-700 sm:h-14 sm:w-14">
                      <Star className={`h-7 w-7 ${rating > index ? 'fill-amber-400 text-amber-600' : 'text-gray-400'}`} aria-hidden />
                    </span>
                  </label>
                ))}
              </div>
              <p aria-live="polite" className="mt-2 min-h-5 text-sm text-gray-600">{rating ? `${rating} de 5 · ${labels[rating - 1]}` : 'Elige de 1 a 5 estrellas'}</p>
            </fieldset>

            <label className="block text-base font-semibold text-gray-900" htmlFor="reviewText">Tu experiencia
              <textarea id="reviewText" name="reviewText" required minLength={10} maxLength={3000} rows={5} className={`${inputClass} resize-y font-normal`} aria-describedby="review-text-help" />
              <span id="review-text-help" className="mt-2 block text-sm font-normal leading-relaxed text-gray-600">Puedes contar lo que te gustó y lo que mejorarías. Evita teléfonos, correos y datos de otras personas.</span>
            </label>

            <fieldset disabled={busy || preparingPhotos}>
              <legend className="font-semibold text-gray-900">Fotos de tu experiencia <span className="font-normal text-gray-500">(opcional)</span></legend>
              <p className="mt-1 text-sm text-gray-600">Hasta 3 fotos tuyas. Evita documentos y personas que no hayan autorizado aparecer.</p>
              <div className="mt-3 flex flex-wrap gap-3">
                {photos.map((photo, index) => (
                  <div key={photo.url} className="relative h-20 w-20">
                    {/* Blob local para vista previa; nunca se envía a un optimizador externo. */}
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={photo.url} alt={`Foto ${index + 1} seleccionada`} className="h-20 w-20 rounded-xl border border-gray-200 object-cover" />
                    <button type="button" disabled={busy || preparingPhotos} onClick={() => removePhoto(photo.url)} aria-label={`Quitar foto ${index + 1}`} className="absolute -right-2 -top-2 flex h-11 w-11 items-center justify-center rounded-full bg-gray-900 text-white shadow-sm"><X size={16} aria-hidden /></button>
                  </div>
                ))}
                {photos.length < 3 && (
                  <label className="relative flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-gray-400 bg-white text-xs font-medium text-gray-700 focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-primary-700">
                    <Camera size={22} aria-hidden /><span>{preparingPhotos ? 'Preparando…' : 'Añadir fotos'}</span>
                    <input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={addPhotos} className="absolute inset-0 h-full w-full cursor-pointer opacity-0" aria-label="Añadir hasta 3 fotos de tu experiencia" />
                  </label>
                )}
              </div>
              <p aria-live="polite" className="mt-2 text-xs text-gray-600">{preparingPhotos ? 'Preparando las miniaturas…' : `${photos.length} de 3 fotos · JPG, PNG o WebP`}</p>
              {photos.length > 0 && <label className="mt-4 flex items-start gap-3 text-sm leading-relaxed text-gray-700">
                <input type="checkbox" name="consentPhotoPublic" required className="mt-1 h-5 w-5 shrink-0 accent-primary-700" />
                Estas fotos son mías y autorizo publicarlas con mi reseña{sharedNakawa ? ' en Nakawa y Pa’Cartagena' : ''}. Tengo permiso de las personas que aparecen.
              </label>}
            </fieldset>

            <div className="grid gap-6 sm:grid-cols-2">
              <label className="block font-semibold text-gray-900" htmlFor="authorDisplayName">Nombre público
                <input id="authorDisplayName" name="authorDisplayName" autoComplete="given-name" required minLength={2} maxLength={60} className={`${inputClass} font-normal`} aria-describedby="name-help" />
                <span id="name-help" className="mt-2 block text-sm font-normal text-gray-600">Tu nombre de pila es suficiente.</span>
              </label>
              <label className="block font-semibold text-gray-900" htmlFor="tourDate">Fecha del tour
                <input id="tourDate" type="date" name="tourDate" max={getDateInTimeZoneISO()} required className={`${inputClass} min-w-0 font-normal`} />
              </label>
            </div>

            <label className="block font-semibold text-gray-900" htmlFor="contact">Correo o WhatsApp de la reserva
              <input id="contact" name="contact" required maxLength={120} type="text" autoCapitalize="none" autoComplete="off" spellCheck={false} className={`${inputClass} font-normal`} aria-describedby="contact-help" />
              <span id="contact-help" className="mt-2 block text-sm font-normal leading-relaxed text-gray-600">Solo para comprobar tu experiencia; no se publica. Para WhatsApp, incluye el indicativo de país.</span>
            </label>

            <div hidden aria-hidden="true"><label>No completar<input name="_hp" tabIndex={-1} autoComplete="off" /></label></div>
            <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-gray-700">
              <input type="checkbox" name="consentTextPublic" required className="mt-1 h-5 w-5 shrink-0 accent-primary-700" />
              <span>Esta es mi experiencia personal. Autorizo publicar mi nombre, calificación y texto{sharedNakawa ? ' en Nakawa y Pa’Cartagena' : ' en Pa’Cartagena'}, y usar mi contacto para verificar la reseña, según la <Link href={privacyHref} className="underline underline-offset-2">política de privacidad</Link>.</span>
            </label>
            {error && <div ref={messageRef} tabIndex={-1} role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 outline-none">{error}</div>}
            <button type="submit" disabled={busy || preparingPhotos} className="flex min-h-14 w-full items-center justify-center gap-2 rounded-full bg-primary-700 px-6 py-3.5 text-base font-semibold text-white transition-colors hover:bg-primary-800 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-700 disabled:opacity-60">
              {busy && <Loader2 className="h-5 w-5 animate-spin motion-reduce:animate-none" aria-hidden />}
              {busy ? 'Enviando…' : 'Enviar mi reseña'}
            </button>
            <p className="text-center text-sm text-gray-600">La reseña se publica después de verificar la experiencia.</p>
          </form>
        )}

        <details className="mt-9 border-t border-gray-200 py-5 text-sm leading-relaxed text-gray-600">
          <summary className="cursor-pointer py-2 font-semibold text-gray-800">Cómo publicamos las opiniones</summary>
          <p className="mt-3">Comprobamos que hayas participado en este tour. Publicamos opiniones positivas y negativas con el mismo criterio, sin cambiar su calificación ni reescribirlas. No ofrecemos premios por opinar.</p>
          <p className="mt-3">No publicamos spam, duplicados, datos personales de terceros ni contenido ajeno a la experiencia. Admitimos una reseña por contacto y fecha del tour. Para corregir o retirar tu opinión, usa el contacto de nuestra política de privacidad.</p>
        </details>
      </div>
    </div>
  );
}
